import React from 'react';
import { Shot as BaseShot, Num, Note, Toc } from './parts';

// Инструкция по созданию событий — английский текст
const lang = 'en';
const Shot = (props) => <BaseShot lang={lang} {...props} />;

const GuideContent = () => {
  return (
    <div className="guide-content">
      <p className="guide-lead">
        An event is created in a single pass through the “Create New Event” form. First pick the event type
        and the way races get into it — that decides which fields the form shows. Every step and every
        option is described below with screenshots.
      </p>

      <Toc
        title="Contents"
        items={[
          ['access', 'Before you start'],
          ['choose', 'Choosing the kind of event'],
          ['basics', 'Step 1. Basics: name, description, co-organizers'],
          ['type', 'Step 2. Event type'],
          ['status', 'Step 3. Status and image'],
          ['registration', 'Step 4. Registration and visibility'],
          ['scoring', 'Step 5. Competition and points'],
          ['wildcards', 'Step 6. Wild cards (race and tournament)'],
          ['sync', 'Option A. Races found by search template'],
          ['manual', 'Option B. Races created by other ROUVY users'],
          ['open', 'Option C. Open tournament'],
          ['official', 'Option D. Official event'],
          ['after', 'After creation: editing and co-organizers'],
          ['problems', 'Common errors']
        ]}
      />

      <section id="access">
        <h2>Before you start</h2>
        <ul>
          <li>
            The <strong>“Create Event”</strong> button <Num>1</Num> on the Events page is shown only to accounts
            allowed to create events. If you don't see it, contact an administrator.
          </li>
          <li>The <strong>“Event types”</strong> button <Num>2</Num> opens a short guide to the types.</li>
          <li>
            The <strong>“Open Tournament”</strong> type is available only to accounts with special access. Other
            accounts don't see it in the type list.
          </li>
          <li>For races to be found by search template, the event author or a co-organizer must have ROUVY connected.</li>
        </ul>
        <Shot
         
          name="events-list"
          alt="Events page: “Event types” and “Create Event” buttons"
          caption="The Events page. The pencil icon on an event card (3) opens editing — the event author and administrators see it."
        />
      </section>

      <section id="choose">
        <h2>Choosing the kind of event</h2>
        <p>The kind of event comes from two decisions: the <strong>type</strong> and <strong>how races are added</strong>.</p>

        <h3>Event types</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Routes</th>
                <th>Schedule</th>
                <th>Races per stage limit</th>
                <th>Wild cards</th>
                <th>Use it for</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Group Ride</strong></td>
                <td>1</td>
                <td>Yes</td>
                <td>—</td>
                <td>—</td>
                <td>A social ride or training with friends</td>
              </tr>
              <tr>
                <td><strong>Race</strong></td>
                <td>1</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>A single race, possibly with several starts</td>
              </tr>
              <tr>
                <td><strong>Tournament</strong></td>
                <td>1 or more</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>Yes</td>
                <td>A series of stages on different routes</td>
              </tr>
              <tr>
                <td><strong>Open Tournament</strong></td>
                <td>1 or more</td>
                <td>No, a period only</td>
                <td>—</td>
                <td>—</td>
                <td>Riders ride the routes whenever they like; the best time counts</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="guide-small">“Routes” is how many different ROUVY routes the event can have. One route can have several starts.</p>

        <h3>Ways to add races</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Way</th>
                <th>Who creates races in ROUVY</th>
                <th>Event types</th>
                <th>How to turn on</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><a href="#sync"><strong>A. Search template</strong></a></td>
                <td>The event author or co-organizers</td>
                <td>Group ride, race, tournament</td>
                <td>Default</td>
              </tr>
              <tr>
                <td><a href="#manual"><strong>B. Manually</strong></a></td>
                <td>Any ROUVY user</td>
                <td>Group ride, race, tournament</td>
                <td>The “Races created by other ROUVY users” checkbox</td>
              </tr>
              <tr>
                <td><a href="#open"><strong>C. Routes without a schedule</strong></a></td>
                <td>Each rider for themselves</td>
                <td>Open tournament</td>
                <td>Pick the “Open Tournament” type</td>
              </tr>
              <tr>
                <td><a href="#official"><strong>D. Official event</strong></a></td>
                <td>ROUVY</td>
                <td>Any except open tournament</td>
                <td>The “Official” status</td>
              </tr>
            </tbody>
          </table>
        </div>
        <Note kind="warning">
          The way races are added is chosen at creation and can't be changed afterwards. If you picked the wrong one,
          create the event again.
        </Note>
      </section>

      <section id="basics">
        <h2>Step 1. Basics: name, description, co-organizers</h2>
        <Shot name="create-basics" alt="Top of the create event form" caption="Top of the form for the search template option" />
        <ol>
          <li><Num>1</Num> <strong>Event Name</strong> — required. Without a search template, ROUVY races are searched by this name.</li>
          <li>
            <Num>2</Num> <strong>Races created by other ROUVY users</strong> — turn on only for <a href="#manual">option B</a>.
            The template field then disappears and a race list appears.
          </li>
          <li>
            <Num>3</Num> <strong>Rouvy search template</strong> — the part of the name shared by all your ROUVY races of this event,
            e.g. <code>Autumn Cup</code>. See <a href="#sync">option A</a>.
          </li>
          <li><Num>4</Num> <strong>Co-organizers</strong> — users who can also add races. Their ROUVY races are found by the template too.</li>
          <li><Num>5</Num> <strong>Description</strong> — rules, format, links. Riders see it on the event page.</li>
        </ol>

        <h3>Adding a co-organizer</h3>
        <p>Start typing a name or nickname (2+ characters) and pick the person from the list. Selected users show as chips; the cross removes one.</p>
        <Shot name="co-organizers" alt="Co-organizer search" caption="One co-organizer is added, a second is being picked from the list" />
      </section>

      <section id="type">
        <h2>Step 2. Event type</h2>
        <p>Below the type list the form shows a hint for the selected type. The type decides which fields come next.</p>
        <div className="guide-shot-grid">
          <Shot name="type-group_ride" alt="Group Ride type" caption="Group Ride: one route, no race limit or wild cards" />
          <Shot name="type-race" alt="Race type" caption="Race: one route, adds races per stage and wild cards" />
          <Shot name="type-tournament" alt="Tournament type" caption="Tournament: several routes, same extra fields as a race" />
          <Shot name="type-open_tournament" alt="Open Tournament type" caption="Open Tournament: adds the tournament period" />
        </div>
      </section>

      <section id="status">
        <h2>Step 3. Status and image</h2>
        <Shot name="status-image" alt="Event status and image" />
        <ol>
          <li>
            <Num>1</Num> <strong>Status</strong>: “Custom” fits almost every event. “Official” is for official ROUVY events,
            see <a href="#official">option D</a>.
          </li>
          <li>
            <Num>2</Num> <strong>Image</strong> — the event card cover. Use “Upload Image”, up to 5 MB.
            “Remove Image” clears the selected picture.
          </li>
        </ol>
      </section>

      <section id="registration">
        <h2>Step 4. Registration and visibility</h2>
        <Shot name="registration" alt="Registration settings" caption="A tournament: “Races per stage” exists only for races and tournaments" />
        <ol>
          <li>
            <Num>1</Num> <strong>Registration Closes (hours before start)</strong> — how many hours before the first race
            registration closes. Empty — riders can register after the start too.
          </li>
          <li>
            <Num>2</Num> <strong>Races per stage</strong> (race and tournament) — how many races of one stage a rider can ride for the standings.
            Riders register for specific races, and only those count. Empty — all races count.
          </li>
          <li><Num>3</Num> <strong>Long Event</strong> — registration stays open the whole time, even after all races.</li>
          <li>
            <Num>4</Num> <strong>Hidden event</strong> — only administrators and organizers see it in the event list.
            Results are calculated as usual, and the direct link works for everyone.
          </li>
        </ol>
      </section>

      <section id="scoring">
        <h2>Step 5. Competition and points</h2>
        <Shot name="scoring" alt="Competition and points" caption="Team competition is on — team fields appeared" />
        <ol>
          <li><Num>1</Num> <strong>Individual Competition</strong> and/or <strong>Team Competition</strong>. Both can be on.</li>
          <li><Num>2</Num> <strong>Points for 1st Place</strong> — what a stage winner gets (1000 by default).</li>
          <li><Num>3</Num> <strong>Points Step</strong> — how much less each next place gets (10 by default).</li>
          <li><Num>4</Num> <strong>Team Members Accounted</strong> (team competition only) — how many best results of a team count.</li>
          <li>
            <Num>5</Num> <strong>Max Team Members</strong> (team competition only) — how many riders of one team can register.
            Empty — no limit.
          </li>
          <li><Num>6</Num> <strong>Create</strong> — saves the event and returns to the event list.</li>
        </ol>
        <Note>
          Points for a place: <code>points for 1st place − (place − 1) × step</code>, but not below 0.
          With 1000 and 10: 1st — 1000, 2nd — 990, 10th — 910, 101st and below — 0.
        </Note>
      </section>

      <section id="wildcards">
        <h2>Step 6. Wild cards (race and tournament)</h2>
        <p>A wild card lets a rider ride a stage outside the schedule. The block exists only for custom events of the Race and Tournament types.</p>
        <ol>
          <li><strong>Wild Cards (max count)</strong> — how many wild cards a rider can use in the event. Empty — no wild cards.</li>
          <li>
            <strong>Creation type for wild card</strong> (shown when wild cards are above 0):
            <ul>
              <li><strong>Created by organizer</strong> — the rider gives a date and time, the organizer creates a ROUVY race for it and approves the request.</li>
              <li><strong>Created by participant</strong> — the rider creates a ROUVY race following your requirements and sends the link; the organizer approves or rejects it.</li>
            </ul>
          </li>
          <li>
            <strong>Requirements field</strong> (“Created by participant” only) — what the rider's race must match: route, settings, time.
            Riders see this text when they submit a request.
          </li>
        </ol>
        <div className="guide-shot-grid">
          <Shot name="wildcards-organizer" alt="Wild cards created by organizer" caption="Created by organizer" />
          <Shot name="wildcards-participant" alt="Wild cards created by participant" caption="Created by participant: requirements appear" />
        </div>
        <p>The organizer gets a notification about every request.</p>
      </section>

      <section id="sync">
        <h2>Option A. Races found by search template</h2>
        <p>This is the default. You (or co-organizers) create races in ROUVY, and the system finds them by template and adds them to the event.</p>
        <ol className="guide-steps">
          <li>Create races in ROUVY with a shared part in each name, e.g. <code>Autumn Cup #1</code>, <code>Autumn Cup #2</code>.</li>
          <li>Leave the “Races created by other ROUVY users” checkbox <strong>off</strong>.</li>
          <li>Put the shared part into <strong>“Rouvy search template”</strong>: <code>Autumn Cup</code>. Empty — the event name is used.</li>
          <li>Add co-organizers if others create races too.</li>
          <li>Fill in the remaining steps and click <strong>“Create”</strong>.</li>
          <li>The race search starts in the background right after creation. Races appear within a few minutes — check them in the event editor.</li>
        </ol>
        <p>The event editor shows the sync block and the list of found races:</p>
        <Shot name="edit-sync" alt="Sync and added races" />
        <ol>
          <li><Num>1</Num> <strong>Sync routes</strong> — search again, e.g. after creating new races in ROUVY. You can close the page.</li>
          <li><Num>2</Num> <strong>Refresh list</strong> — load the sync result without reloading the page.</li>
          <li><Num>3</Num> <strong>Delete</strong> — remove an unwanted race from the event.</li>
        </ol>
        <Note>Saving a changed event name or template starts the search automatically.</Note>
      </section>

      <section id="manual">
        <h2>Option B. Races created by other ROUVY users</h2>
        <p>If neither you nor co-organizers created the ROUVY races, the template search won't find them. Add the races manually instead.</p>
        <ol className="guide-steps">
          <li>Turn on <strong>“Races created by other ROUVY users”</strong>. The template field disappears and the “Event races” block appears.</li>
          <li>
            For each race fill in:
            <ul>
              <li><strong>ROUVY route*</strong> — a route ID or a link like <code>https://riders.rouvy.com/route/12345</code>;</li>
              <li><strong>Route name</strong> — optional, if you want your own;</li>
              <li><strong>ROUVY race link*</strong> — <code>https://riders.rouvy.com/events/…</code>;</li>
              <li><strong>Race name</strong> — optional, empty — taken from ROUVY;</li>
              <li><strong>Start date and time*</strong> — in your time zone.</li>
            </ul>
          </li>
          <li><strong>“Add race”</strong> adds a row, <strong>“Remove”</strong> deletes it. Empty rows aren't saved.</li>
          <li>Click <strong>“Create”</strong>. If any race fails, the event isn't created, and the error names the row number.</li>
        </ol>
        <Shot name="manual-create" alt="Event races at creation" caption="The race list when creating the event" />
        <p>You can add races later too: the event editor has a form for one race <Num>1</Num>. An added race shows in the list right away <Num>2</Num>.</p>
        <Shot name="edit-manual" alt="Adding a race in the editor" />
      </section>

      <section id="open">
        <h2>Option C. Open tournament</h2>
        <p>
          An open tournament has no schedule. Each rider creates a ROUVY race (Race) on a tournament route and rides it whenever they like.
          A ride counts by route and race type, and the best time of all attempts goes to the standings.
        </p>
        <ol className="guide-steps">
          <li>Pick <strong>“Open Tournament”</strong> in “Event Type”.</li>
          <li>
            Set the <strong>tournament period</strong>: “Tournament starts” and “Tournament ends”. Rides outside it don't count.
            Both are optional: without them the tournament never ends. The end must be after the start.
          </li>
          <li>Fill in the remaining steps and click <strong>“Create”</strong>.</li>
          <li>Open the event editor and add routes (see below).</li>
        </ol>
        <Note kind="warning">Open tournament routes are added only after the event is created, in the event editor.</Note>
        <Shot name="edit-open" alt="Open tournament routes" />
        <ol>
          <li><Num>1</Num> Paste a ROUVY route page link: <code>https://riders.rouvy.com/route/…</code>.</li>
          <li><Num>2</Num> Click <strong>“Add route”</strong>.</li>
          <li><Num>3</Num> The route joins the tournament. “Delete” removes it.</li>
        </ol>
      </section>

      <section id="official">
        <h2>Option D. Official event</h2>
        <p>
          The <strong>“Official”</strong> status is for official ROUVY events. ROUVY manages their races, so the form is shorter:
          no manual races checkbox, search template, co-organizers or wild cards.
        </p>
        <Shot name="official" alt="Official event form" caption="“Official” status — extra blocks are hidden" />
      </section>

      <section id="after">
        <h2>After creation: editing and co-organizers</h2>
        <ul>
          <li>Open the editor with the pencil icon on the event card on the Events page. All settings can be changed by the event author and administrators.</li>
          <li>The way races are added (the “Races created by other ROUVY users” checkbox) can't be changed in the editor.</li>
          <li>Co-organizers see a simplified page: they can add races (run the sync or enter them manually) and delete the ones they added. Other settings aren't available to them.</li>
        </ul>
        <Shot name="co-organizer-view" alt="Co-organizer page" caption="What a co-organizer sees: only their own race can be deleted" />
      </section>

      <section id="problems">
        <h2>Common errors</h2>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Message or situation</th>
                <th>Cause and fix</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>“Event name is required”</td>
                <td>Fill in “Event Name”.</td>
              </tr>
              <tr>
                <td>“Group Ride and Race events can only have one route”</td>
                <td>The event has races on different routes. Pick the Tournament type or remove extra races.</td>
              </tr>
              <tr>
                <td>“The tournament must end after it starts”</td>
                <td>Fix the open tournament period.</td>
              </tr>
              <tr>
                <td>“Enter the route, the ROUVY race link and the start date/time for each race”</td>
                <td>A manual race row misses a required field. Fill it in or remove the row.</td>
              </tr>
              <tr>
                <td>Open tournament is refused when saving</td>
                <td>The account has no open tournament access. Pick another type.</td>
              </tr>
              <tr>
                <td>No races after sync</td>
                <td>
                  Check that the ROUVY race names contain the template, the races were created by you or co-organizers,
                  and your ROUVY is connected. Wait a few minutes and click “Refresh list”.
                  If someone else created the races, you need <a href="#manual">option B</a> — create the event again.
                </td>
              </tr>
              <tr>
                <td>No “Create Event” button</td>
                <td>The account isn't allowed to create events — contact an administrator.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default GuideContent;
